import { ReactComponent as CaretIcon } from '@icons/arrow_down.svg';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

// Helpers
import { getClassNames, renderNameIcon } from '@utils/helpers';

// Components
import { DemoGuidePopoverContext } from '@/context/demoGuidePopover';
import { getNavigationOptions } from '@/hooks/useRolePermission';
import { getActiveOrganization, getUserOrganization } from '@/redux/slice/authSlice';
import { browserSelector } from '@/redux/slice/browserType.slice';
import { ORGANIZATION_TYPES } from '@/utils/constants';
import DemoGuidePopover from '../DemoGuidePopover';
import SwitchOrganization, { organizationType } from '../GlobalModals/SwitchOrganization';
import { Button } from '../button';
import { ReactComponent as HomePrompt } from '@icons/home-prompt.svg';
import { PromptTypeEnum } from '@/interface/Organization.interface';

const SideBar = ({ activeNav }: { activeNav?: number }) => {
  const [navigateId, setNavigateId] = useState<number | null>(activeNav ?? null);
  const [orgViewModal, setOrgViewModal] = useState(false);
  const { navOptions1, navOptions2 } = getNavigationOptions();
  const activeOrganizationData = useSelector(getActiveOrganization);
  const userOrganizationData = useSelector(getUserOrganization);
  const browserType = useSelector(browserSelector);
  const { popUp1, setPopUp1, setPopUp2 } = React.useContext(DemoGuidePopoverContext);
  const [networkOrganization, setNetworkOrganization] = useState<organizationType | null>(
    null
  );

  useEffect(() => {
    if (activeNav && navigateId && activeNav !== navigateId) {
      setNavigateId(activeNav);
    }
  }, [activeNav]);

  return (
    <div
      className={getClassNames(
        'min-h-screen bg-primary flex flex-col transition-all border border-stroke',
        'min-w-[245px]'
      )}
    >
      {orgViewModal && (
        <SwitchOrganization
          isOpen={orgViewModal}
          closeModal={setOrgViewModal}
          networkOrganization={networkOrganization}
          setNetworkOrganization={setNetworkOrganization}
        />
      )}
      <div className="pl-6 h-[61px] flex items-center border-b border-theme-stroke">
        <div className="body-black  ">
          <div className="flex items-center gap-2.5">
            {renderNameIcon({
              name: activeOrganizationData?.name ?? '',
              className:
                'w-[22px] h-[22px] bg-grey-content-quaternary text-lg font-medium leading-5 text-white',
              textClassName:
                ' flex items-center ml-2 text-body-black text-lg font-medium leading-5',
              isLimitLongText: true,
              imageUrl: activeOrganizationData?.image ?? '',
            })}
          </div>
        </div>
      </div>
      <div className="flex-1 bg-primary">
        <div
          className="bg-primary mt-5 pl-6 pr-4 flex flex-col gap-y-0.5 pb-4"
          id="target1"
        >
          <DemoGuidePopover
            title="Home"
            body="The operational dashboard supporting merchant operations teams with consumer fraud data, analytics and tools to prevent fraud."
            el={() => document.getElementById('target1')}
            topOffset={40}
            leftOffset={25}
            arrowPosition="right"
            noArrow={true}
            maxWidth="max-w-[468px]"
            icon={<HomePrompt />}
            open={popUp1}
            setOpen={setPopUp1}
            setNext={setPopUp2}
            promptBlock={PromptTypeEnum.DASHBOARD}
          />
          {/* <DemoGuidePopover
            title="Projects"
            el={() => document.getElementById('target1')}
            topOffset={-40}
            leftOffset={230}
            arrowPosition="left"
            open={popUp6}
            setOpen={setPopUp6}
            setPrev={
              activeProject?.products === 'CHECKOUT_AND_REFUND_MANAGER'
                ? setPopUp5
                : setPopUp4
            }
          >
            <div>
              <p className="text-grey-content-tertiary mt-3">
                Let’s set up a project to configure the fraud risk algorithm and
                personalised payments experiences.
              </p>
              <button
                className="mt-2 text-white2 text-sm1 py-1 underline"
                onClick={() => {
                  navigate(PRIVATE_NAVIGATION.projects.manage.path);
                  setPopUp6(false);
                }}
              >
                Go to Projects
              </button>
            </div>
          </DemoGuidePopover> */}
          {navOptions1.map((item) => (
            <Link to={item.to} key={item.id}>
              <span
                className={getClassNames(
                  navigateId === item.id
                    ? 'text-primary bg-active-nav/5'
                    : 'text-secondary',
                  item.to
                    ? 'hover:text-primary hover:bg-active-nav/5'
                    : 'cursor-default opacity-50',
                  'flex items-center justify-between w-full py-2 px-2 rounded-[5px]',
                  'font-medium text-base1'
                )}
                onClick={() => {
                  setNavigateId(item.id);
                }}
              >
                <span>{item.name}</span>
              </span>
            </Link>
          ))}
        </div>

        <hr className="border-theme-stroke" />
        <div className="pt-5 pl-6 pr-4 flex flex-col gap-y-0.5 pb-4">
          <div
            className={getClassNames(
              'text-grey-content-tertiary',
              'cursor-default opacity-50 flex items-center justify-between w-full',
              'py-2 px-2 rounded-[5px] font-medium text-base1'
            )}
          >
            <span>Help & Resources</span>
          </div>
          {navOptions2.map((item) =>
            item.to ? (
              <Link
                to={item.to}
                key={item.id}
                target={
                  item.isExternal && browserType?.isChromeBrowser ? '_blank' : '_self'
                }
                onClick={() => {
                  setNavigateId(item.id);
                }}
              >
                <span
                  className={getClassNames(
                    navigateId === item.id
                      ? 'text-primary bg-active-nav/5'
                      : 'text-secondary',
                    item.to
                      ? 'hover:text-primary hover:bg-active-nav/5'
                      : 'cursor-default opacity-50',
                    'flex items-center justify-between w-full py-2 px-2 rounded-[5px]',
                    'font-medium text-base1'
                  )}
                >
                  <span>{item.name}</span>
                </span>
              </Link>
            ) : (
              <span
                className={getClassNames(
                  navigateId === item.id
                    ? 'text-primary bg-active-nav/5'
                    : 'text-secondary',
                  'hover:text-primary hover:bg-active-nav/5 cursor-pointer',
                  'flex items-center justify-between w-full py-2 px-2 rounded-[5px]',
                  'font-medium text-base1'
                )}
                onClick={() => {
                  if (item.onClick) item.onClick();
                  setNavigateId(item.id);
                }}
              >
                <span>{item.name}</span>
              </span>
            )
          )}
        </div>
      </div>
      {userOrganizationData?.organization_type !== ORGANIZATION_TYPES.NODE && (
        <div className="h-10 px-8 border-t border-stroke-primary">
          <Button
            title={`${activeOrganizationData?.organization_type !== ORGANIZATION_TYPES.NETWORK_MASTER ? activeOrganizationData?.name : 'Switch organisation'}`}
            className="border-0 w-full flex justify-between text-body-black text-base bg-white2"
            suffixIcon={<CaretIcon />}
            onClick={async () => {
              setOrgViewModal(true);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default SideBar;
